
import {
  toggle,
  apiToggle,
  combineReducers
} from './common';

import paginate from './paginate';

import {
  UPDATE_SUBSCRIPTION_SUCCESS,
  UPDATE_SUBSCRIPTION_ERROR,
  REQUEST_SUBSCRIPTION,
  REQUEST_SUBSCRIPTION_ERROR,
  REQUEST_SUBSCRIPTION_SUCCESS,
  SYNC_SUBSCRIPTION_SUCCESS,
  CANCEL_SUBSCRIPTION,
  CANCEL_SUBSCRIPTION_SUCCESS,
  CANCEL_SUBSCRIPTION_ERROR,
  REACTIVATE_SUBSCRIPTION_SUCCESS,
  REACTIVATE_SUBSCRIPTION_ERROR,
  UPDATE_SUBSCRIPTION,
  REACTIVATE_SUBSCRIPTION,
  SYNC_SUBSCRIPTION_ERROR,
  SYNC_SUBSCRIPTION,
  REQUEST_SUBSCRIPTION_INVITATIONS,
  REQUEST_SUBSCRIPTION_INVITATIONS_SUCCESS,
  REQUEST_SUBSCRIPTION_USERS,
  REQUEST_SUBSCRIPTION_USERS_SUCCESS,
  INVITE_USERS,
  INVITE_USERS_SUCCESS,
  HIDE_SUBSCRIPTION_GREETING,
  DISPLAY_SUBSCRIPTION_GREETING,
  REQUEST_SUBSCRIPTION_DOMAINS,
  REQUEST_SUBSCRIPTION_DOMAINS_SUCCESS,
  DISMISS_SUBSCRIPTION_WARNING_SUCCESS,
  ADD_USER,
  REMOVE_USER,
} from '../actions/subscriptions';
import { REQUEST_ACCOUNT_SUCCESS } from '../actions/users';
import { CLEAR_MESSAGES, CLEAR_ERRORS } from '../actions/messages';
import { INVITE_DOMAIN_ERROR, REMOVE_DOMAIN_SUCCESS } from '../actions/domains';

const MESSAGES = {
  // Unused, in favor of subscription.greeting flag (see BP-28)
  CREATED: 'subscription.messages.created',
  CANCELLED: 'subscription.messages.cancelled',
  REACTIVATED: 'subscription.messages.reactivated',
  UPDATED: 'subscription.messages.updated',
  PAYMENT_INFO_UPDATED: 'subscription.messages.paymentInfoUpdated',
  INVITES_SENT: 'subscription.invitations.info.invitesSent',
  NO_INVITES_SENT: 'subscription.invitations.info.noInvitesSent',
};

const messages = (state = [], action) => {
  switch (action.type) {
    case UPDATE_SUBSCRIPTION:
    case REACTIVATE_SUBSCRIPTION:
    case CANCEL_SUBSCRIPTION:
    case SYNC_SUBSCRIPTION:
    case CLEAR_MESSAGES:
      return [];
    case CANCEL_SUBSCRIPTION_SUCCESS:
      return state.concat([MESSAGES.CANCELLED])
    case REACTIVATE_SUBSCRIPTION_SUCCESS:
      return state.concat([MESSAGES.REACTIVATED])
    case UPDATE_SUBSCRIPTION_SUCCESS:
      return state.concat([MESSAGES.UPDATED])
    case SYNC_SUBSCRIPTION_SUCCESS:
      return state.concat([MESSAGES.PAYMENT_INFO_UPDATED])
    default:
      return state;
  }
}

const errors = (state = [], action) => {
  switch (action.type) {
    case UPDATE_SUBSCRIPTION:
    case REACTIVATE_SUBSCRIPTION:
    case CANCEL_SUBSCRIPTION:
    case SYNC_SUBSCRIPTION:
    case CLEAR_ERRORS:
      return [];
    case UPDATE_SUBSCRIPTION_ERROR:
    case REACTIVATE_SUBSCRIPTION_ERROR:
    case CANCEL_SUBSCRIPTION_ERROR:
    case SYNC_SUBSCRIPTION_ERROR:
    case INVITE_DOMAIN_ERROR:
      return state.concat(action.error.errors);
    default:
      return state;
  }
}

function subscriptionInvitations(state, action) {
  switch (action.type) {
    case INVITE_USERS:
      return {
        ...state,
        ...paginate(state, action),
        sent: []
      };
    case INVITE_USERS_SUCCESS:
      return {
        ...state,
        ...paginate(state, action),
        sent: action.invitations,
        messages: action.invitations.length > 0 
          ? [MESSAGES.INVITES_SENT] 
          : [MESSAGES.NO_INVITES_SENT]
      };
    case CLEAR_MESSAGES:
      return {
        ...state,
        ...paginate(state, action),
        messages: [],
      };
    case REQUEST_SUBSCRIPTION_INVITATIONS:
    case REQUEST_SUBSCRIPTION_INVITATIONS_SUCCESS:
      return {
        ...state,
        ...paginate(state, action)
      };
    default:
      return state;
  }
}

function subscriptionUsers(state, action) {
  switch (action.type) {
    case REQUEST_SUBSCRIPTION_USERS:
    case REQUEST_SUBSCRIPTION_USERS_SUCCESS:
      return paginate(state, action);
    default:
      return state;
  }
}

function subscriptionDomains(state, action) {
  switch (action.type) {
    case REQUEST_SUBSCRIPTION_DOMAINS:
    case REQUEST_SUBSCRIPTION_DOMAINS_SUCCESS:
    case REMOVE_DOMAIN_SUCCESS:
      return paginate(state, action);
    default:
      return state;
  }
}

function updatingOn(state = null, action) {
  switch (action.type) {
    case UPDATE_SUBSCRIPTION_ERROR:
    case UPDATE_SUBSCRIPTION_SUCCESS:
      return null
    case UPDATE_SUBSCRIPTION:
      return action.planId  
    default:
      return state;
  }
}

function warnings(state, action) {
  switch (action.type) {
    case REQUEST_SUBSCRIPTION_SUCCESS:
      return action.subscription.warnings ? [ ...action.subscription.warnings ] : []
    case DISMISS_SUBSCRIPTION_WARNING_SUCCESS:
      return state.filter(alert => alert.id !== action.alertId)
    default:
      return state
  }
}



const clientStates = combineReducers({
  warnings,
  invitations: subscriptionInvitations,
  users: subscriptionUsers,
  domains: subscriptionDomains,
  messages,
  errors,
  updatingOn,
  fetching: apiToggle(REQUEST_SUBSCRIPTION),
  cancelling: apiToggle(CANCEL_SUBSCRIPTION),
  reactivating: apiToggle(REACTIVATE_SUBSCRIPTION),
  inviting: apiToggle(INVITE_USERS),
  switching: apiToggle(ADD_USER) || apiToggle(REMOVE_USER),
  notFound: toggle({
    [REQUEST_SUBSCRIPTION_ERROR]: true,
    [REQUEST_SUBSCRIPTION_SUCCESS]: false
  }),
  greeting: toggle({
    [DISPLAY_SUBSCRIPTION_GREETING]: true,
    [HIDE_SUBSCRIPTION_GREETING]: false
  }),
  usersCount: (state = 0, action) => {
    if(action && action.subscription && action.subscription.usersCount !== undefined) {
      return action.subscription.usersCount;
    }
    return state;
  }
})

const subscription = (state = {}, action) => ({
  ...state,
  ...action.subscription,
  ...clientStates(state, action)
});

const withId = id => (state = {}, action) => ({
  ...state,
  [id]: {
    id,
    ...subscription(state[id], action)
  }
})

const add = (state, subscription) =>
  withId(subscription.id)(state, { subscription });

export default function subscriptions(state = {}, action) {
  const { subscriptionId } = action;
  const sub = action.subscription;
  const id = (sub && sub.id) || subscriptionId;

  if (id) {
    return withId(id)(state, action);
  }

  switch (action.type) {
    case REQUEST_ACCOUNT_SUCCESS:
      const { account } = action;
      if (account.subscriptions) {
        return account.subscriptions
          .reduce(add, state);
      }
      return state;
    case CLEAR_MESSAGES:
    case CLEAR_ERRORS:
      return Object.keys(state)
        .reduce((memo, id) => {
          memo[id] = subscription(state[id], action);
          return memo;
        }, state);
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/subscriptions.js